import { DistributionMetrics } from '../../../../hooks/useExecutiveData';

interface Props {
  distribution: DistributionMetrics;
}

const DistribTable = ({
  title, rows,
  valueColor = 'text-violet-400',
}: {
  title: string;
  rows: { label: string; count: number; pct: number }[];
  valueColor?: string;
}) => {
  const maxPct = Math.max(...rows.map((r) => r.pct), 1);
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <h3 className="text-[14px] font-600 text-white mb-4">{title}</h3>
      <div className="space-y-2.5">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="flex justify-between text-[12px] mb-1">
              <span className="text-white/50">{row.label}</span>
              <span className={`font-600 ${valueColor}`}>{row.count} <span className="text-white/30">({row.pct}%)</span></span>
            </div>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full bg-violet-500/60 transition-all duration-500`}
                style={{ width: `${Math.max((row.pct / maxPct) * 100, row.count > 0 ? 3 : 0)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DistributionSection = ({ distribution }: Props) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <DistribTable
        title="Distribuição por faixa de saldo"
        rows={distribution.byBalanceRange}
        valueColor="text-cyan-400"
      />
      <DistribTable
        title="Distribuição por nº de contas"
        rows={distribution.byAccountCount}
        valueColor="text-indigo-400"
      />
      <DistribTable
        title="Distribuição por nº de cartões"
        rows={distribution.byCardCount}
        valueColor="text-pink-400"
      />
      <DistribTable
        title="Distribuição por nº de lançamentos"
        rows={distribution.byTransactionCount}
        valueColor="text-emerald-400"
      />
      <DistribTable
        title="Distribuição por pendências"
        rows={distribution.byPendingCount}
        valueColor="text-orange-400"
      />
    </div>

    {/* Plan engagement comparison */}
    {distribution.byPlanEngagement.length > 0 && (
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10">
          <h3 className="text-[14px] font-600 text-white">Engajamento comparado por plano</h3>
          <p className="text-[12px] text-white/40 mt-0.5">Visão agregada e anônima — sem dados individuais identificáveis</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-[12px] text-white/40 font-500 px-5 py-3">Plano</th>
                <th className="text-right text-[12px] text-white/40 font-500 px-5 py-3">Média de lançamentos</th>
                <th className="text-right text-[12px] text-white/40 font-500 px-5 py-3">Média de metas</th>
                <th className="text-right text-[12px] text-white/40 font-500 px-5 py-3">Taxa de uso</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {distribution.byPlanEngagement.map((p) => (
                <tr key={p.planName} className="hover:bg-white/[0.03] transition-colors">
                  <td className="px-5 py-3">
                    <span className="text-[13px] text-white font-500">{p.planName}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-[13px] text-cyan-400 font-600">{p.avgTx}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="text-[13px] text-violet-400 font-600">{p.avgGoals}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500/60 rounded-full"
                          style={{ width: `${p.pctActive}%` }}
                        />
                      </div>
                      <span className={`text-[13px] font-600 ${p.pctActive >= 50 ? 'text-emerald-400' : p.pctActive >= 20 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {p.pctActive}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);
